'use client';

import Icon from '@/components/ui/AppIcon';
import DemoDataPill from './DemoDataPill';
import type {
  QBReportResponse,
  QBARAgingDetailReport,
  QBAPAgingDetailReport,
  IntuitReportResponse,
  IntuitReportRow,
} from '@/lib/quickbooks/types';

interface ReportState<T> {
  data: QBReportResponse<T> | null;
  loading: boolean;
  error: string | null;
}

interface Props {
  arAging: ReportState<QBARAgingDetailReport>;
  apAging: ReportState<QBAPAgingDetailReport>;
  onRetryAR: () => void;
  onRetryAP: () => void;
}

// ── Shared types + parsing ────────────────────────────────────────────────

interface AgingRow {
  party: string;
  reference: string;
  dueDate: string;
  agingBucket: string;
  amount: number;
}

function parseAgingRows(report: IntuitReportResponse | undefined): AgingRow[] {
  if (!report?.Rows?.Row) return [];
  const out: AgingRow[] = [];
  const walk = (rows: IntuitReportRow[]) => {
    for (const row of rows) {
      if (row.type === 'Data' && row.ColData) {
        out.push({
          party:       row.ColData[0]?.value ?? '',
          reference:   row.ColData[1]?.value ?? '',
          dueDate:     row.ColData[2]?.value ?? '',
          agingBucket: row.ColData[3]?.value ?? '',
          amount:      parseFloat(row.ColData[4]?.value ?? '0') || 0,
        });
      }
      if (row.Rows?.Row) walk(row.Rows.Row);
    }
  };
  walk(report.Rows.Row);
  return out;
}

const BUCKETS = ['Current', '1-30', '31-60', '61-90', '90+'] as const;
const BUCKET_ALIASES: Record<string, typeof BUCKETS[number]> = {
  'Current': 'Current',
  '1-30':    '1-30',
  '1–30':    '1-30',
  '31-60':   '31-60',
  '31–60':   '31-60',
  '61-90':   '61-90',
  '61–90':   '61-90',
  '90+':     '90+',
};

function normalizeBucket(s: string): typeof BUCKETS[number] {
  return BUCKET_ALIASES[s.trim()] ?? 'Current';
}

interface BucketTotal {
  bucket: typeof BUCKETS[number];
  amount: number;
  count: number;
}

function bucketize(rows: AgingRow[]): BucketTotal[] {
  const init: Record<typeof BUCKETS[number], BucketTotal> = {
    'Current': { bucket: 'Current', amount: 0, count: 0 },
    '1-30':    { bucket: '1-30',    amount: 0, count: 0 },
    '31-60':   { bucket: '31-60',   amount: 0, count: 0 },
    '61-90':   { bucket: '61-90',   amount: 0, count: 0 },
    '90+':     { bucket: '90+',     amount: 0, count: 0 },
  };
  for (const r of rows) {
    const b = normalizeBucket(r.agingBucket);
    init[b].amount += r.amount;
    init[b].count += 1;
  }
  return BUCKETS.map((b) => init[b]);
}

function bucketColor(b: typeof BUCKETS[number]): string {
  if (b === 'Current') return '#10B981';
  if (b === '1-30')    return '#07B0E9';
  if (b === '31-60')   return '#F59E0B';
  if (b === '61-90')   return '#EF4444';
  return '#B91C1C';
}

function daysFromIso(iso: string): number {
  if (!iso) return 0;
  const todayMs = new Date(new Date().toISOString().slice(0, 10) + 'T00:00:00Z').getTime();
  const isoMs = new Date(iso + 'T00:00:00Z').getTime();
  return Math.floor((todayMs - isoMs) / 86_400_000);
}

function formatMoney(val: number): string {
  if (Math.abs(val) >= 1_000_000) return `$${(val / 1_000_000).toFixed(2)}M`;
  if (Math.abs(val) >= 1_000)     return `$${(val / 1_000).toFixed(1)}k`;
  return `$${val.toFixed(0)}`;
}

function staleLabel(fetchedAt: string): string {
  const mins = Math.floor((Date.now() - new Date(fetchedAt).getTime()) / 60_000);
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h`;
}

// ── Component ─────────────────────────────────────────────────────────────

export default function Row4ARAPDetail({ arAging, apAging, onRetryAR, onRetryAP }: Props) {
  return (
    <section className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <ARCard arAging={arAging} onRetry={onRetryAR} />
      <APCard apAging={apAging} onRetry={onRetryAP} />
    </section>
  );
}

// ── AR ─────────────────────────────────────────────────────────────────────

function ARCard({ arAging, onRetry }: { arAging: ReportState<QBARAgingDetailReport>; onRetry: () => void }) {
  const isLoading = arAging.loading && !arAging.data;
  const isError = !arAging.loading && arAging.error && !arAging.data;
  const rows = parseAgingRows(arAging.data?.data);
  const buckets = bucketize(rows);
  const total = rows.reduce((s, r) => s + r.amount, 0);
  const mock = !!arAging.data?.mock;
  const stale = !!arAging.data?.staleOnError;

  // Sort: most overdue first (highest positive daysFromIso)
  const sorted = [...rows].sort((a, b) => daysFromIso(b.dueDate) - daysFromIso(a.dueDate)).slice(0, 12);

  return (
    <div className="relative rounded-xl p-5" style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}>
      {mock && <DemoDataPill />}
      <div className="flex items-center justify-between gap-2 mb-3 pr-16">
        <div className="flex items-center gap-2">
          <Icon name="DocumentCurrencyDollarIcon" size={15} style={{ color: '#F59E0B' }} />
          <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Accounts Receivable
          </h3>
        </div>
        <p className="text-[11px] text-gray-500 font-semibold whitespace-nowrap">
          {formatMoney(total)} outstanding · {rows.length} {rows.length === 1 ? 'invoice' : 'invoices'}
        </p>
      </div>
      {stale && arAging.data && (
        <p className="text-[10px] italic text-gray-400 mb-2">
          Data may be stale · refreshed {staleLabel(arAging.data.fetchedAt)} ago
        </p>
      )}

      {isLoading ? (
        <LoadingBody />
      ) : isError ? (
        <ErrorBody error={arAging.error!} onRetry={onRetry} tag="[AR]" />
      ) : rows.length === 0 ? (
        <EmptyBody message="No outstanding invoices — all customers paid up" />
      ) : (
        <>
          <BucketBars buckets={buckets} total={total} />
          <div className="mt-3 max-h-[280px] overflow-y-auto space-y-1.5">
            {sorted.map((r, i) => {
              const overdue = daysFromIso(r.dueDate);
              const tone = arOverdueColor(overdue);
              return (
                <button
                  key={`${r.reference}-${i}`}
                  className="w-full text-left rounded-lg px-2.5 py-2 hover:bg-gray-50 transition-colors"
                  style={{ border: '1px solid #F3F4F6' }}
                  suppressHydrationWarning
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">{r.party}</p>
                      <p className="text-[10px] text-gray-500 font-mono truncate">{r.reference} · due {r.dueDate}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs font-bold text-gray-700">{formatMoney(r.amount)}</span>
                      <span className="rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ background: tone.bg, color: tone.fg }}>
                        {overdue > 0 ? `${overdue}d over` : `${-overdue}d`}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function arOverdueColor(daysOverdue: number): { fg: string; bg: string } {
  if (daysOverdue > 90)  return { fg: '#7F1D1D', bg: 'rgba(239,68,68,0.18)' };
  if (daysOverdue > 60)  return { fg: '#B91C1C', bg: 'rgba(239,68,68,0.10)' };
  if (daysOverdue > 30)  return { fg: '#92400E', bg: 'rgba(245,158,11,0.10)' };
  return                       { fg: '#047857', bg: 'rgba(16,185,129,0.10)' };
}

// ── AP ─────────────────────────────────────────────────────────────────────

function APCard({ apAging, onRetry }: { apAging: ReportState<QBAPAgingDetailReport>; onRetry: () => void }) {
  const isLoading = apAging.loading && !apAging.data;
  const isError = !apAging.loading && apAging.error && !apAging.data;
  const rows = parseAgingRows(apAging.data?.data);
  const buckets = bucketize(rows);
  const total = rows.reduce((s, r) => s + r.amount, 0);
  const mock = !!apAging.data?.mock;
  const stale = !!apAging.data?.staleOnError;

  // Sort: most urgent first (smallest daysFromIso — most negative = furthest in future = least urgent; most positive = overdue first)
  const sorted = [...rows].sort((a, b) => daysFromIso(b.dueDate) - daysFromIso(a.dueDate)).slice(0, 12);

  return (
    <div className="relative rounded-xl p-5" style={{ background: '#FFFFFF', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0' }}>
      {mock && <DemoDataPill />}
      <div className="flex items-center justify-between gap-2 mb-3 pr-16">
        <div className="flex items-center gap-2">
          <Icon name="CreditCardIcon" size={15} style={{ color: '#EF4444' }} />
          <h3 className="text-base font-bold" style={{ color: '#000150', fontFamily: 'Inter, Poppins, sans-serif' }}>
            Accounts Payable
          </h3>
        </div>
        <p className="text-[11px] text-gray-500 font-semibold whitespace-nowrap">
          {formatMoney(total)} due · {rows.length} {rows.length === 1 ? 'bill' : 'bills'}
        </p>
      </div>
      {stale && apAging.data && (
        <p className="text-[10px] italic text-gray-400 mb-2">
          Data may be stale · refreshed {staleLabel(apAging.data.fetchedAt)} ago
        </p>
      )}

      {isLoading ? (
        <LoadingBody />
      ) : isError ? (
        <ErrorBody error={apAging.error!} onRetry={onRetry} tag="[AP]" />
      ) : rows.length === 0 ? (
        <EmptyBody message="No bills due — caught up on payables" />
      ) : (
        <>
          <BucketBars buckets={buckets} total={total} />
          <div className="mt-3 max-h-[280px] overflow-y-auto space-y-1.5">
            {sorted.map((r, i) => {
              const daysOver = daysFromIso(r.dueDate);
              const tone = apUrgencyColor(daysOver);
              return (
                <button
                  key={`${r.reference}-${i}`}
                  className="w-full text-left rounded-lg px-2.5 py-2 hover:bg-gray-50 transition-colors"
                  style={{ border: '1px solid #F3F4F6' }}
                  suppressHydrationWarning
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-800 truncate">{r.party}</p>
                      <p className="text-[10px] text-gray-500 font-mono truncate">{r.reference} · due {r.dueDate}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs font-bold text-gray-700">{formatMoney(r.amount)}</span>
                      <span className="rounded-full px-2 py-0.5 text-[9px] font-bold" style={{ background: tone.bg, color: tone.fg }}>
                        {daysOver > 0 ? `${daysOver}d over` : `${-daysOver}d to pay`}
                      </span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

function apUrgencyColor(daysOver: number): { fg: string; bg: string } {
  if (daysOver > 0)    return { fg: '#7F1D1D', bg: 'rgba(239,68,68,0.18)' };  // overdue
  const daysUntil = -daysOver;
  if (daysUntil <= 7)  return { fg: '#92400E', bg: 'rgba(245,158,11,0.15)' };
  if (daysUntil <= 14) return { fg: '#0369A1', bg: 'rgba(7,176,233,0.10)' };
  return                      { fg: '#047857', bg: 'rgba(16,185,129,0.10)' };
}

// ── Shared sub-views ──────────────────────────────────────────────────────

function BucketBars({ buckets, total }: { buckets: BucketTotal[]; total: number }) {
  return (
    <div className="space-y-1.5">
      {buckets.map((b) => {
        const pct = total > 0 ? (b.amount / total) * 100 : 0;
        const color = bucketColor(b.bucket);
        return (
          <div key={b.bucket} className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wider font-semibold text-gray-500 w-12">{b.bucket}</span>
            <div className="flex-1 h-2 rounded-full" style={{ background: '#F3F4F6' }}>
              <div className="h-2 rounded-full" style={{ width: `${pct}%`, background: color }} />
            </div>
            <span className="text-[11px] font-semibold text-gray-700 w-14 text-right">
              {formatMoney(b.amount)}
            </span>
            <span className="text-[10px] text-gray-400 w-6 text-right">{b.count}</span>
          </div>
        );
      })}
    </div>
  );
}

function LoadingBody() {
  return (
    <div className="py-8 flex items-center justify-center">
      <div className="w-5 h-5 rounded-full border-[2.5px] border-[#07B0E9] border-t-transparent animate-spin" />
    </div>
  );
}

function ErrorBody({ error, onRetry, tag }: { error: string; onRetry: () => void; tag: string }) {
  return (
    <div className="rounded-lg p-3 flex items-start gap-2" style={{ background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)' }}>
      <Icon name="ExclamationTriangleIcon" size={16} style={{ color: '#B91C1C', flexShrink: 0 }} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold" style={{ color: '#7F1D1D' }}>{tag} fetch failed</p>
        <p className="text-[11px] text-gray-600 mt-0.5 break-words">{error}</p>
        <button
          onClick={onRetry}
          className="mt-2 rounded-md px-2.5 py-1 text-[11px] font-semibold text-white hover:opacity-90 active:scale-95 transition"
          style={{ background: '#EF4444' }}
        >
          Retry
        </button>
      </div>
    </div>
  );
}

function EmptyBody({ message }: { message: string }) {
  return (
    <div className="rounded-lg p-3 flex items-center gap-2" style={{ background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.18)' }}>
      <Icon name="CheckCircleIcon" size={16} style={{ color: '#10B981' }} />
      <p className="text-xs font-medium" style={{ color: '#047857' }}>{message}</p>
    </div>
  );
}
